import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Button,
  Box,
  Link,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

function SubscriptionPage() {
  const { t } = useTranslation();
  const [userData, setUserData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Function to extract token from URL and send to backend
  const getTokenAndSendToBackend = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const encodedToken = urlParams.get("token");

      if (!encodedToken) {
        throw new Error("Token not found in URL");
      }

      const decodedToken = decodeURIComponent(encodedToken);
      const url = `${process.env.REACT_APP_VR_WEB_HOOK_URL}/resolvepurchased`;
      const codeParam = `${process.env.REACT_APP_VR_WEB_HOOK_TOKEN}`;
      const fullUrl = `${url}?code=${codeParam}`;

      const response = await fetch(fullUrl, {
        method: "POST",
        headers: {
          "x-ms-marketplace-token": decodedToken,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(
          response.status === 400
            ? "Invalid or expired token"
            : `Error: ${response.statusText}`
        );
      }

      const data = await response.json();
      setUserData(data);
    } catch (error) {
      console.error("Fetch error:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTokenAndSendToBackend();
  }, []);

  // Render customer actions
  const renderCustomerActions = () => {
    if (!userData.subscriptionState) return null;

    const actions = {
      PendingFulfillmentStart: {
        link: `/notify-activated?id=${userData.id}`,
        text: t("subscription.notifyActivation"),
      },
      Subscribed: {
        link: `/unsubscribe?id=${userData.id}`,
        text: t("subscription.unsubscribe"),
      },
    };

    const action = actions[userData.subscriptionState];
    return action ? (
      <>
        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          {t("subscription.actions")}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate(action.link)}
          sx={{ mt: 1 }}
        >
          {action.text}
        </Button>
      </>
    ) : null;
  };

  // Translate subscription state
  const getTranslatedSubscriptionState = (state) => {
    return t(`subscriptionStates.${state}`) || state;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="50vh"
      >
        <Box sx={{ pr: 2 }}>
          <CircularProgress size={60} />
        </Box>
        <Typography variant="h6">{t("loading")}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Paper elevation={3} sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h5" color="error">
          {t("error.tokenInvalid")}
        </Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          {t("error.tokenError")}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t("subscription.title")}
      </Typography>
      <Typography variant="body1" component={"p"}>
        {t("subscription.verificationMessage")}
      </Typography>

      <TableContainer>
        <Table>
          <TableBody>
            {[
              {
                label: t("subscription.details.currentUser"),
                value: userData.currentUser || t("subscription.details.unknown"),
              },
              {
                label: t("subscription.details.email"),
                value: userData.email || t("subscription.details.unknown"),
              },
              {
                label: t("subscription.details.subscriptionName"),
                value: userData.subscriptionName || t("subscription.details.unknown"),
              },
              {
                label: t("subscription.details.planName"),
                value: userData.planName ? userData.planName : t("subscription.details.unknown"),
              },
              {
                label: t("subscription.details.subscriptionState"),
                value: getTranslatedSubscriptionState(
                  userData.subscriptionState
                ),
              },
            ].map(({ label, value }) => (
              <TableRow key={label}>
                <TableCell component="th" scope="row">
                  {label}
                </TableCell>
                <TableCell>{value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {renderCustomerActions()}

      <Box sx={{ pt: 2, display: "flex" }}>
        <Typography sx={{ pr: 1 }} variant="body1">
          {t("support.haveQuestions")}
        </Typography>
        <Link
          target="_blank"
          href={process.env.REACT_APP_SUPPORT_PAGE}
          variant="body1"
        >
          {t("support.visitSite")}
        </Link>
      </Box>
    </Paper>
  );
}

export default SubscriptionPage;
